/**
 * PATH: packages/ui/src/kanban/components/KanbanCardOverlays.tsx
 * FILE_ID: UI-KANBAN-CARD-OVERLAYS-001
 * PURPOSE: The absolutely-positioned overlays that sit on top of a KanbanCard:
 *   the multi-select corner badge and the three drop/flash states
 *   (email-allocate, just-allocated, nest-under).
 * WHY: Extracted from KanbanCard.tsx (≤500-line working limit). Pure
 *   presentation — flags in, overlay JSX out; renders a Fragment so the card
 *   DOM is byte-identical to the inline version.
 * RELATED:
 *   - ./KanbanCard.tsx (renders this as the first children of the root).
 *   - ./useKanbanCardInteractions.ts (source of the drop flags).
 */
import { Check, CornerDownRight } from 'lucide-react'

import type { Card as KanbanCardModel } from '../types'

export interface KanbanCardOverlaysProps {
  card: KanbanCardModel
  selected: boolean
  emailDropHover: boolean
  justAllocated: boolean
  nestDropHover: boolean
}

export function KanbanCardOverlays({
  selected,
  emailDropHover,
  justAllocated,
  nestDropHover,
}: KanbanCardOverlaysProps) {
  return (
    <>
      {/* FEAT-2026-06-15-KANBAN-MULTISELECT — selection feedback. The blue
          ring (cardRing) marks the edge; this adds (a) a soft full-card blue
          WASH so a selected card reads as highlighted, not just outlined, and
          (b) a corner check so the set stays unmistakable even mid-drag,
          where native HTML5 DnD gives no drag-count feedback.
          BUG-2026-06-29: operator said the bare tick "is not good" — the wash
          is the primary signal now. */}
      {selected && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 rounded-xl bg-blue-500/12 dark:bg-blue-400/15"
        />
      )}
      {selected && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -left-1.5 -top-1.5 z-20 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white shadow ring-2 ring-white dark:ring-zinc-900"
        >
          <Check className="h-2.5 w-2.5" strokeWidth={3} />
        </span>
      )}
      {/* Allocate-email drop overlay — visible only while a row
          from the right rail is hovering this card. Centered
          chip says "Allocate email here" so first-time operators
          understand the drop will create an email→card link
          (qf!2507). */}
      {emailDropHover && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-emerald-500/10 backdrop-blur-[1px]"
        >
          <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white shadow">
            Allocate email here
          </span>
        </div>
      )}
      {/* Post-drop success flash — emerald "✓ Allocated" pill shown
          for ~1.4s after an email is dropped + linked to this card.
          Gives the native-DnD gesture the confirmation it otherwise
          lacks. Suppressed while a fresh hover is active so the two
          emerald states don't stack. */}
      {justAllocated && !emailDropHover && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-emerald-500/10"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white shadow">
            <Check className="h-3 w-3" /> Allocated
          </span>
        </div>
      )}
      {/* Stack 12 MR-C — Nest-under drop overlay. Restricted to the card's
          RIGHT-EDGE band (2026-06-22 operator request): nesting now only
          fires when a dragged card is held over the right edge, so the
          highlight points exactly at that drop zone. Releasing anywhere
          else drops the card as a sibling in the lane. Violet to
          distinguish from the emerald email-allocate state. */}
      {nestDropHover && (
        <div
          aria-hidden="true"
          data-testid="kanban-nest-edge"
          className="pointer-events-none absolute inset-y-0 right-0 z-10 flex w-[38%] min-w-[48px] max-w-[120px] items-center justify-end gap-1 rounded-r-xl border-r-2 border-violet-500 bg-gradient-to-l from-violet-500/30 via-violet-500/12 to-transparent pr-1.5"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow">
            <CornerDownRight className="h-3 w-3" /> Nest
          </span>
        </div>
      )}
    </>
  )
}
